import {
  Alert,
  AnimatedContentLoader,
  Badge,
  Button,
  DocumentWidget,
  FileAnalysisBadges,
  FilePreview2,
  HeaderBlock,
  Input,
  Prompt,
  Tab,
  Tabs,
  TextArea
} from '@components'
import { getInvoicingPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission, usePermissionsLoaded } from '@context'
import {
  deleteFile,
  fetchCombinedInvoices,
  AttachmentMalwareScanStatus,
  fetchFile,
  ICombinedInvoice,
  IFile,
  IInvoice,
  IPurchaseInvoice,
  updateFile
} from '@query'
import { formatDate, setAPIErrors } from '@utils'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  FaCloudDownloadAlt,
  FaFileInvoice,
  FaInfoCircle,
  FaSave,
  FaStream,
  FaTrashAlt
} from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { AttachmentAnalysisPreview } from '@pages/FilesPage/components/AnalysisPreview.tsx'
import { useDebugMode } from '@hooks'
import { downloadFileToBeSaved } from '@root/utils/downloadFileToBeSaved'
import { Link } from 'react-router-dom'

interface Props {
  fileId: number
  onClose: () => void
}

interface FormInputs {
  name: string
  tags: { name: string }[]
  notes: string
}

const invoiceLinkAndDisplayName = (invoice: ICombinedInvoice, slug: string): [string, string] => {
  const invoiceId = invoice.item.id
  if (invoice.type === 'purchase') {
    const purchaseInvoice = invoice.item as IPurchaseInvoice
    const displayName = `${formatDate(purchaseInvoice.invoicing_date)} ${
      purchaseInvoice.sender_name
    }`
    const link = getInvoicingPageUrl(slug, {
      id: invoiceId,
      type: 'purchase'
    })
    return [link, displayName]
  } else if (invoice.type === 'sales') {
    const salesInvoice = invoice.item as IInvoice
    const displayName = `${formatDate(salesInvoice.invoicing_date)} ${
      salesInvoice.receiver_info.name
    }`
    const link = getInvoicingPageUrl(slug, {
      id: invoiceId,
      type: 'sales'
    })
    return [link, displayName]
  }
}

export const FileDetailView: React.FC<Props> = ({ fileId, onClose }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const [isDebugModeOn] = useDebugMode()
  const [showDeletePrompt, setShowDeletePrompt] = React.useState(false)
  const hasEditPermission = usePermission(PermissionSet.FILES_EDIT)
  const hasBookkeepingViewPermission = usePermission(PermissionSet.BOOKKEEPING_VIEW)
  const permissionsLoaded = usePermissionsLoaded()
  const canFetchRelatedInvoice = usePermission(PermissionSet.INVOICING_VIEW)
  const queryClient = useQueryClient()

  const {
    mutateAsync: deleteMutation,
    isPending: isDeleting,
    isSuccess: isDeleted
  } = useMutation({
    mutationFn: () => deleteFile({ fileId, slug }),
    ...{
      onSuccess: async () => {
        setShowDeletePrompt(false)
        onClose()
        await queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
      }
    }
  })

  const {
    data,
    isLoading: isFileLoading,
    refetch
  } = useQuery({
    queryKey: [slug, 'files', fileId],
    queryFn: () => fetchFile({ slug, fileId }),
    enabled: !(isDeleting || isDeleted),
    refetchInterval: query =>
      query.state.data?.malware_scan_status === AttachmentMalwareScanStatus.PENDING ? 2000 : false
  })

  const form = useForm<FormInputs>({
    defaultValues: async () => {
      const { data } = await refetch()
      if (!data) {
        return { name: '', tags: [], notes: '' }
      }

      return { name: data.name, tags: [], notes: data.notes ?? '' }
    }
  })
  const {
    register,
    setError,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting, isLoading: isFormLoading }
  } = form

  const { data: invoiceData, isLoading: isInvoiceDataLoading } = useQuery({
    queryKey: [slug, 'invoice', fileId],
    queryFn: () => fetchCombinedInvoices({ slug }, { original_attachment_id: fileId }),
    ...{
      enabled: !(isDeleting || isDeleted) && permissionsLoaded && canFetchRelatedInvoice
    }
  })

  const showInvoiceLink = invoiceData?.results?.length == 1
  const [invoiceLink, invoiceDisplayName] = showInvoiceLink
    ? invoiceLinkAndDisplayName(invoiceData.results[0], slug)
    : ['', '']

  const updateMutation = useMutation<IFile, unknown, Partial<IFile>>({
    mutationFn: data => updateFile({ slug, fileId }, data),
    ...{
      onSuccess: updatedFile => {
        queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
        reset({
          name: updatedFile.name,
          tags: [],
          notes: updatedFile.notes ?? ''
        })
      },
      onError: error => {
        const { status, data } = (error as any) ?? {}
        status === 400 && setAPIErrors(data, setError)
      }
    }
  })

  const onSubmit = handleSubmit(formData =>
    updateMutation.mutate({
      name: formData.name,
      notes: formData.notes.trim() ? formData.notes.trim() : null
    })
  )

  const isFormDataLoading = isFileLoading || isFormLoading
  const isDetailsLoading = isFormDataLoading || isInvoiceDataLoading

  return (
    <>
      <FileDetailWrapper>
        <HeaderBlock
          header={t('files.fileDetails.header')}
          subHeader={<></>}
          menuItems={[
            {
              Icon: FaCloudDownloadAlt,
              name: t('files.fileDetails.download'),
              onClick: () =>
                downloadFileToBeSaved(slug, fileId, data?.name, t('general.downloadError'))
            },
            ...(hasEditPermission
              ? [
                  {
                    Icon: FaTrashAlt,
                    name: t('files.fileDetails.delete'),
                    intent: 'danger',
                    onClick: () => setShowDeletePrompt(true),
                    disabled: isDeleting
                  }
                ]
              : [])
          ]}
        />

        <FileDetailContent>
          <Tabs>
            <Tab key="info" icon={<FaInfoCircle />} name={t('files.fileDetails.info')}>
              <AnimatedContentLoader isLoading={isDetailsLoading}>
                <PreviewWrapper>{data && <FilePreview2 file={data} />}</PreviewWrapper>
                <BadgeContainer>
                  {showInvoiceLink && (
                    <StyledInvoiceBadgeLink to={invoiceLink}>
                      <Badge type="default">
                        <InvoiceBadgeContent>
                          <FaFileInvoice />
                          {invoiceDisplayName}
                        </InvoiceBadgeContent>
                      </Badge>
                    </StyledInvoiceBadgeLink>
                  )}
                  <FileAnalysisBadges badges={data?.analysis_badges} fileId={data?.id} />
                </BadgeContainer>

                {isDebugModeOn && <AttachmentAnalysisPreview attachment={data} />}

                <FileDetailForm key={fileId} onSubmit={onSubmit}>
                  <fieldset disabled={!hasEditPermission}>
                    <Input
                      id="file-name"
                      label={t('files.fileDetails.fileName')}
                      {...register('name', {
                        required: { value: true, message: t('validation.required') }
                      })}
                      errors={errors.name}
                      required={true}
                    />
                    {hasEditPermission && (
                      <TextArea
                        id="file-notes"
                        label={t('files.notes.label')}
                        placeholder={t('files.notes.placeholder')}
                        {...register('notes')}
                        errors={errors.notes}
                      />
                    )}
                  </fieldset>
                  {!hasEditPermission && (
                    <NotesReadOnly>
                      <NotesLabel>{t('files.notes.label')}</NotesLabel>
                      <NotesValue>{data?.notes?.trim() || t('files.notes.empty')}</NotesValue>
                    </NotesReadOnly>
                  )}
                  <StyledFooter>
                    {hasEditPermission && (
                      <Button
                        icon={<FaSave />}
                        intent="success"
                        type="submit"
                        disabled={!isDirty || isFormDataLoading}
                        showSpinner={isSubmitting}
                      >
                        {t('general.save')}
                      </Button>
                    )}
                  </StyledFooter>
                </FileDetailForm>
              </AnimatedContentLoader>
            </Tab>

            {hasBookkeepingViewPermission && (
              <Tab key="docs" icon={<FaStream />} name={t('files.fileDetails.attachedHeader')}>
                <DocumentWidget
                  header={t('files.fileDetails.attachedDescription')}
                  noData={t('files.fileDetails.attachedEmpty')}
                  fileId={data?.id}
                  height={400}
                />
              </Tab>
            )}
          </Tabs>
        </FileDetailContent>
      </FileDetailWrapper>
      <Prompt
        title={t('files.fileDetails.delete')}
        description={
          <DeletePromptContent>
            <div>{t('general.areYouSure')}</div>
            <Alert
              type="error"
              title={t('files.fileDetails.cannotDelete')}
              isVisible={!data?.is_deletable}
              description={t('files.fileDetails.cannotDeleteDescription')}
            />
          </DeletePromptContent>
        }
        isVisible={showDeletePrompt}
        onClose={() => setShowDeletePrompt(false)}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setShowDeletePrompt(false),
            disabled: isDeleting
          },
          {
            text: t('general.delete'),
            icon: <FaTrashAlt />,
            intent: 'danger',
            action: () => deleteMutation(),
            showSpinner: isDeleting,
            disabled: !data?.is_deletable || isDeleting
          }
        ]}
      />
    </>
  )
}

const PreviewWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem 0;
`

const FileDetailWrapper = styled.div`
  height: 100%;
  background: white;
  overflow: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`

const FileDetailContent = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const FileDetailForm = styled.form`
  width: 100%;
`

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const BadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const StyledInvoiceBadgeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  width: fit-content;
`

const InvoiceBadgeContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  svg {
    width: 8px;
    height: 8px;
    flex-shrink: 0;
  }
`

const DeletePromptContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const NotesReadOnly = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const NotesLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const NotesValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  white-space: pre-wrap;
`
